/*
========================================
PRODUCT PREVIEW

BLOCK: HEADER

Отвечает за:
- название товара
- мини-картинки товара
- выделение активной картинки
- горизонтальный скрытый скролл
  если картинок больше 4
- выбор картинки по клику

Не содержит:
- центральную картинку
- шторки
- цену
========================================
*/

import Image from "next/image"
import { clx } from "@medusajs/ui"
import Link from "next/link"

type ProductPreviewHeaderProps = {
  title?: string | null
  handle?: string | null
  gallery: { url: string | null }[]
  activeIndex: number
  setActiveIndex: (index: number) => void
}

export default function ProductPreviewHeader({
  title,
  handle,
  gallery,
  activeIndex,
  setActiveIndex,
}: ProductPreviewHeaderProps) {
  return (
    <>
      <div
        className="
          group
          relative
          mb-3
          pt-1
          perspective-1000
        "
      >
        {/* основная синяя кнопка-заголовок */}

<div
  className="
    relative
    min-h-[42px]
    overflow-visible
    rounded-3xl
    border border-white/80
    bg-gradient-to-b
    from-[#3a5f88]
    via-[#284469]
    to-[#1b2b46]
    px-3 py-2
    shadow-[0_6px_10px_rgba(0,0,0,0.45),0_2px_3px_rgba(0,0,0,0.35)]
    ring-1 ring-white/20
    before:absolute before:inset-0
    before:rounded-3xl
    before:bg-gradient-to-b
    before:from-white/25
    before:via-white/5
    before:to-transparent
    before:pointer-events-none
    transition-all
    duration-300
    hover:-translate-y-1
    hover:shadow-[0_18px_18px_rgba(0,0,0,0.35),0_10px_10px_rgba(0,0,0,0.25)]
    hover:brightness-110
    group-hover:-translate-y-[1px]
  "
>
  
  {/* 1. ТЕКСТ */}
  <div className="relative z-10 flex items-center justify-center pointer-events-none">
    <span className="
      text-center
      text-[15px]
      sm:text-[14px]
      font-black
      uppercase
      tracking-[0.08em]
      text-white/95
      drop-shadow-[0_2px_3px_rgba(0,0,0,0.8)]
      [text-shadow:0_1px_2px_rgba(0,0,0,1)]
    ">
      {title || "PRODUCT"}
    </span>
  </div>
  
  {/* 3. ТВОЯ КРАСИВАЯ ПОЛОСКА (Возвращена во всей красе) */}
  <span
  className="
    pointer-events-none
    absolute
    left-1/2
    top-[80%]
    h-[3px]
    w-[88%]
    -translate-x-1/2
    overflow-hidden
    rounded-full
    bg-gradient-to-r
    from-transparent
    via-sky-200
    to-transparent
    blur-[0.6px]
    shadow-[0_0_12px_rgba(186,230,253,0.9),0_0_22px_rgba(96,165,250,0.45)]
    opacity-90
  "
>

{/* 2. ЛУЧ ФОНАРИКА (Сделали ярче: 0.45 -> 0.8 и расширили центр) */}

  <span
    className="
      absolute
      top-0
      left-[-22%]
      h-full
      w-[22%]
      skew-x-[-20deg]
      bg-gradient-to-r
      from-transparent
      via-white
      to-transparent
      opacity-95
      animate-title-line-shine
    "
  />
</span>

  {/* 4. ССЫЛКА */}
  <Link
    href={handle ? `/products/${handle}` : "#"}
    className="absolute inset-0 z-30 rounded-3xl active:scale-[0.98] transition-transform"
  />
</div>

</div>    

        
     

      {/* Галерея картинок с усиленными 3D-тенями */}
      {gallery.length > 1 && (
        <div
          className="
            no-scrollbar
            mb-3 overflow-x-auto py-4 pl-3
          "
        >
          <div
            className="
              flex min-w-max gap-5 pr-6
            "
          >
            {gallery.map((img, index) => {
              const isActive = index === activeIndex

              return (
                <button
                  key={`${img.url}-${index}`}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={clx(
                    `
                      relative
                      h-[82px]
                      w-[82px]
                      shrink-0
                      overflow-hidden
                      rounded-xl
                      transition-all
                      duration-300
                    `,
                    "shadow-[0_4px_4px_rgba(0,0,0,0.8),0_2px_2px_rgba(0,0,0,0.6)]",
                    "hover:-translate-y-1 hover:shadow-[0_9px_9px_rgba(0,0,0,0.4),0_9px_9px_rgba(0,0,0,0.25)]",
                    {
                      "z-10 translate-y-[-2px] scale-110 border-2 border-orange-500 ring-4 ring-orange-500/20 shadow-[0_8px_8px_rgba(0,0,0,0.32),0_4px_8px_rgba(0,0,0,0.22)]":
                        isActive,
                      "border border-white/10 opacity-90": !isActive,
                    }
                  )}
                  aria-label={`Show image ${index + 1}`}
                >
                  {img.url && (
                    <Image
                      src={img.url}
                      alt="Product preview"
                      fill
                      className="
                        object-cover
                      "
                      sizes="82px"
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
